import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { IconButton } from 'shared/ui/IconButton'
import { useUnifiedAgentContext } from 'pages/chatbot/ui/UnifiedAgent/context/unifiedAgentContext'
import { useWidgetKeyboard } from 'pages/chatbot/ui/UnifiedAgent/hooks/useWidgetKeyboard'
import { getLastStepHintMessage } from 'pages/chatbot/ui/UnifiedAgent/lib/getLastStepHintMessage'
import {
  WidgetCard,
  WidgetFooter,
  WidgetHint,
} from 'pages/chatbot/ui/UnifiedAgent/ui/Chat/ui/Widgets'
import { ActionsWidgetStore } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/store/actionsWidgetStore'
import { ActionsStep1 } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/Steps/ActionsStep1'
import { ActionsStep2 } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/Steps/ActionsStep2'
import { ActionsStep3Tag } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/Steps/ActionsStep3Tag'
import { ActionsStep4 } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/Steps/ActionsStep4'
import { ActionsStep3SMS } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/Steps/ActionsStep3SMS'
import { ActionsStep3Note } from 'pages/chatbot/ui/UnifiedAgent/ui/InstructionsSection/ui/Steps/ActionsStep3Note'

const DEFAULT_HINT = ['↑↓ to navigate', 'Enter to select', 'Esc to skip']

export const ActionsWidget = observer(() => {
  const { instructionsSectionStore, chatStore, entities } = useUnifiedAgentContext()
  const [store] = useState(
    () => new ActionsWidgetStore(instructionsSectionStore, chatStore, entities)
  )

  useEffect(() => {
    return () => {
      store.reset()
    }
  }, [])

  const isNavigableStep = store.step === 1 || store.step === 2
  const isActionStep = store.isTagStep || store.isNoteStep || store.isSMSStep

  useWidgetKeyboard({
    onNavigate: isNavigableStep ? (dir) => store.moveFocus(dir) : undefined,
    onEnter: isNavigableStep ? store.handleSelect : undefined,
    onNext: isActionStep && !store.isNextDisabled ? store.advanceStep : undefined,
    onSubmit: store.isLastStep && !store.isNextDisabled ? store.handleSubmit : undefined,
    onEscape: store.handleClose,
    isLastStep: store.isLastStep,
  })

  const hintItems = () => {
    if (isActionStep) return getLastStepHintMessage([])
    return store.isLastStep ? getLastStepHintMessage([]) : DEFAULT_HINT
  }

  const renderContent = () => {
    if (store.step === 1) {
      return <ActionsStep1 focusedIndex={store.safeIndex} onSelect={store.selectAt} />
    }
    if (store.step === 2) {
      return <ActionsStep2 focusedIndex={store.safeIndex} onSelect={store.selectAt} />
    }
    if (store.isTagStep) {
      return <ActionsStep3Tag tagsControl={store.tagsControl} canCreate={store.isAddTagStep} />
    }
    if (store.isSMSStep) {
      return <ActionsStep3SMS control={store.smsAutoControl} contentStore={store.smsContentStore} />
    }
    if (store.isNoteStep) {
      return (
        <ActionsStep3Note control={store.noteAutoControl} contentStore={store.noteContentStore} />
      )
    }
    if (store.isLastStep) {
      return <ActionsStep4 value={store.triggerText} onChange={store.setTriggerText} />
    }
    return null
  }

  return (
    <>
      <WidgetCard header={store.currentHeader}>
        {renderContent()}
        <WidgetFooter
          step={store.step}
          total={store.totalSteps}
          onSkip={store.handleClose}
          onBack={store.step > 1 ? store.goBack : undefined}
          action={
            isActionStep ? (
              <IconButton
                icon={'arrowRight'}
                ariaLabel={'WidgetNext'}
                color={'primary'}
                onClick={store.advanceStep}
                disabled={store.isNextDisabled}
              />
            ) : store.isLastStep ? (
              <IconButton
                icon={'arrowUp'}
                ariaLabel={'WidgetSubmit'}
                color={'primary'}
                onClick={store.handleSubmit}
                disabled={store.isNextDisabled}
              />
            ) : null
          }
        />
      </WidgetCard>
      <WidgetHint items={hintItems()} />
    </>
  )
})
